import { useRef, useState } from 'react'
import { TimePicker } from '@mantine/dates'
import styles from './TimeInput.module.scss'

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/
const TIME_PICKER_CLASS_NAMES = {
  fieldsRoot: styles.fieldsRoot,
  fieldsGroup: styles.fieldsGroup,
  field: styles.field,
  dropdown: styles.dropdown,
  control: styles.control
}
const TIME_PICKER_PROPS = {
  format: '24h',
  withDropdown: true,
  hoursStep: 1,
  minutesStep: 30
}

const padTimePart = value => value.toString().padStart(2, '0')

const normalizeTimeValue = value => {
  if (!value) return null

  const [hours, minutes] = value.split(':')
  const timeValue = `${hours}:${minutes}`

  return TIME_PATTERN.test(timeValue) ? timeValue : null
}

const timeToMinutes = value => {
  const [hours, minutes] = value.split(':').map(Number)
  return hours * 60 + minutes
}

const clampTime = (value, minTime, maxTime) => {
  if (!TIME_PATTERN.test(value)) return value

  const valueMinutes = timeToMinutes(value)
  if (minTime && maxTime && timeToMinutes(minTime) > timeToMinutes(maxTime)) return maxTime
  if (minTime && valueMinutes < timeToMinutes(minTime)) return minTime
  if (maxTime && valueMinutes > timeToMinutes(maxTime)) return maxTime
  return value
}

const roundToHalfHour = value => {
  if (!TIME_PATTERN.test(value)) return value

  const [hours, minutes] = value.split(':').map(Number)
  let roundedHours = hours
  let roundedMinutes = 0

  if (minutes >= 15 && minutes < 45) {
    roundedMinutes = 30
  } else if (minutes >= 45) {
    roundedHours = (hours + 1) % 24
  }

  return `${padTimePart(roundedHours)}:${padTimePart(roundedMinutes)}`
}

// Thin wrapper around Mantine TimePicker that:
//   - uses 24h HH:mm values
//   - exposes the existing open/close controls used by TimeWindow
//   - snaps valid typed values to the closest half hour on blur
const TimeInput = ({
  value,
  onChange,
  open,
  onOpen,
  onClose,
  disabled,
  className,
  minTime,
  maxTime
}) => {
  const [pickerRevision, setPickerRevision] = useState(0)
  const hoursInputRef = useRef(null)
  const minutesInputRef = useRef(null)

  const handleChange = nextValue => {
    const normalizedValue = normalizeTimeValue(nextValue)
    if (normalizedValue) onChange(normalizedValue)
  }

  const handleFocus = () => {
    if (!disabled) onOpen()
  }

  const getCurrentInputValue = () => {
    const hours = hoursInputRef.current?.value
    const minutes = minutesInputRef.current?.value
    const inputValue = `${hours}:${minutes}`

    return TIME_PATTERN.test(inputValue) ? inputValue : value
  }

  const commitRoundedValue = () => {
    const latestValue = getCurrentInputValue()
    const roundedValue = clampTime(roundToHalfHour(latestValue), minTime, maxTime)
    if (roundedValue !== latestValue) {
      setPickerRevision(revision => revision + 1)
    }
    if (roundedValue !== value) {
      onChange(roundedValue)
    }
    onClose()
  }

  const handleBlurCapture = event => {
    if (!event.currentTarget.contains(event.relatedTarget)) commitRoundedValue()
  }

  const popoverProps = {
    opened: open,
    withinPortal: true,
    zIndex: 7000,
    onChange: opened => {
      if (!opened) onClose()
    }
  }

  return (
    <div
      className={className}
      onClick={() => !disabled && onOpen()}
      onBlurCapture={handleBlurCapture}
    >
      <TimePicker
        key={pickerRevision}
        {...TIME_PICKER_PROPS}
        min={minTime}
        max={maxTime}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        hoursRef={hoursInputRef}
        minutesRef={minutesInputRef}
        popoverProps={popoverProps}
        className={styles.picker}
        classNames={TIME_PICKER_CLASS_NAMES}
        disabled={disabled}
      />
    </div>
  )
}

export default TimeInput
